import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Divider,
  Autocomplete,
  Chip,
  Checkbox,
  ListItemText
} from '@mui/material';
import {
  Close as CloseIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { resolveDatePeriodRange, DATE_PERIOD_LABELS } from '../../utils/datePeriods';
import { ThemeContext } from '../ThemeContext/ThemeContext';
import { connect } from 'react-redux';
import { getUsers } from '../../redux/user/user.actions';
import ClientSearch from '../ClientSearch';
import ServicePicker from '../ServicePicker';
import { ROTULOS, VALOR_GRUPO_DO_AGENTE, VALOR_STATUS_NAO_CONCLUIDO, ehValorDinamico, opcoesComDinamicos } from '../../utils/filtroDinamico';
import { opcoesDaBuscaAvancada } from '../../utils/opcoesDaBuscaAvancada';
import { comoLista, temSelecao } from '../../utils/selecaoMultipla';
import { mesmoCliente, rotuloDoCliente } from '../../utils/clienteDoFiltro';
import http from '../../services/http';

const emptyFilterState = {
    title: '',
    status: '',
    // Listas, e não string: prioridade, categoria e canal aceitam mais de uma
    // opção marcada (ponto 11 da Gisele, 27/10/2026). O estado antigo, gravado
    // quando eram de escolha única, é uma string e volta do `localStorage` --
    // `comoLista` normaliza os dois na hora de desenhar.
    priority: [],
    assignedTo: null,
    createdBy: null,
    // Cliente e serviço entraram na mesma conversa, com a mesma razão:
    // "quero ver os chamados de três clientes" e "quero ver dois serviços
    // juntos" não podem exigir um Kanban para cada combinação.
    client: [],
    category: [],
    contact: '',
    channel: [],
    ultimaAcao: '',
    /*
     * O TEXTO escrito dentro de uma ação do chamado.
     *
     * É o único campo da Busca Avançada que só existe no SERVIDOR: o payload
     * da listagem traz o chamado, não a conversa dele, então o navegador não
     * tem como avaliá-lo. Quando ele está preenchido, a lista vem do
     * servidor -- o mesmo caminho da caixa de pesquisa.
     */
    textoNaAcao: '',
    ccUsers: null,
    createdAtStart: null,
    createdAtEnd: null,
    resolvedAtStart: null,
    resolvedAtEnd: null,
    updatedAtStart: null,
    updatedAtEnd: null,
    // O departamento entrou na mesma conversa, e pelo mesmo motivo: sem ele,
    // ver dois departamentos juntos exigia um Kanban para cada.
    serviceGroupId: [],
    serviceId: [],
    product: '',
    linhaDoProduto: '',
    representative: '',
};

const AdvancedSearchModal = ({
  open,
  onClose,
  onApply,
  onClear,
  users = [],
  getUsers,
  fixedClient = null,
  scopedTicketsForMeta = null,
}) => {
  const { theme } = useContext(ThemeContext);
  
  const [filters, setFilters] = useState({ ...emptyFilterState });

  const [categories, setCategories] = useState([]);
  const [channels, setChannels] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [serviceGroups, setServiceGroups] = useState([]);
  const [services, setServices] = useState([]);
  const [ticketStatuses, setTicketStatuses] = useState([]);
  const [products, setProducts] = useState([]);
  const [productLines, setProductLines] = useState([]);
  const [representatives, setRepresentatives] = useState([]);

  const loadUniqueValues = useCallback(async () => {
    try {
      // Status reais cadastrados (Resolvido, Cancelado, Em atendimento, etc.),
      // em vez da lista legada fixa (Aberto/Pendente/Fechado).
      const ssr = await http.get('/ticket-statuses');
      setTicketStatuses(Array.isArray(ssr.data) ? ssr.data : []);
    } catch (e) {
      console.error('Erro ao carregar status:', e);
      setTicketStatuses([]);
    }

    try {
      const sr = await http.get('/services');
      setServices(Array.isArray(sr.data) ? sr.data : []);
    } catch (e) {
      console.error('Erro ao carregar serviços:', e);
      setServices([]);
    }

    try {
      const gr = await http.get('/groups');
      const list = Array.isArray(gr.data) ? gr.data : [];
      setServiceGroups(
        list
          .filter((g) => g && (g.status === true || g.status === 1))
          .sort((a, b) => String(a.description || '').localeCompare(String(b.description || ''), 'pt'))
      );
    } catch (e) {
      console.error('Erro ao carregar grupos de atendimento:', e);
      setServiceGroups([]);
    }

    // As listas dos seletores saem do SERVIDOR, em duas consultas pequenas.
    //
    // Antes saíam de um `GET /ticket` SEM escopo, que devolve TODOS os chamados
    // já criados -- 266 mil em produção, com as relações completas. O processo
    // do PHP morria, o Apache devolvia a própria página de 500, e sem PHP vivo
    // não saíam cabeçalhos de CORS: o console mostrava erro de CORS que não
    // tinha nada a ver com CORS. O `catch` aqui só fazia `console.error`, então
    // o efeito visível era Categoria, Contato, Produto e Representante
    // VAZIOS, sem nada na tela explicando. Ficou assim de 01/10 a 12/10.
    //
    // Há teste varrendo este arquivo para o `GET /ticket` não voltar
    // (`utils/opcoesDaBuscaAvancada.test.js`).
    let camposDoServidor = null;
    try {
      const fr = await http.get('/filters/available/fields');
      camposDoServidor = fr.data || null;
    } catch (e) {
      console.error('Erro ao carregar os campos de condição:', e);
    }

    let registeredCategoryNames = [];
    try {
      const cr = await http.get('/ticket-categories');
      const list = Array.isArray(cr.data) ? cr.data : [];
      registeredCategoryNames = list
        .map((c) => (typeof c === 'string' ? c : c?.name))
        .filter(Boolean);
    } catch (e) {
      console.error('Erro ao carregar categorias cadastradas:', e);
    }

    // Os chamados entram apenas como ACRÉSCIMO, e só quando a tela já os tem em
    // mãos: o `ClientTicketsPage` passa os dele, que são poucos e já carregados.
    // A tela de chamados não passa nada, e é exatamente por isso que o modal
    // não pode depender deles.
    const opcoes = opcoesDaBuscaAvancada(
      camposDoServidor,
      registeredCategoryNames,
      Array.isArray(scopedTicketsForMeta) ? scopedTicketsForMeta : null
    );

    setChannels(opcoes.canais);
    setCategories(opcoes.categorias);
    setProducts(opcoes.produtos);
    setProductLines(opcoes.linhasDeProduto);
    setRepresentatives(opcoes.representantes);
    setContacts(opcoes.contatos);
  }, [scopedTicketsForMeta]);

  useEffect(() => {
    if (open) {
      if (!users || users.length === 0) {
        getUsers();
      }
      loadUniqueValues();
      if (fixedClient) {
        // Sempre LISTA, nunca o objeto solto: desde que o campo aceita vários,
        // um valor que às vezes é objeto e às vezes lista é uma pergunta a
        // mais em cada lugar que o lê.
        setFilters({ ...emptyFilterState, client: [fixedClient] });
      }
    }
  }, [open, users, getUsers, fixedClient, loadUniqueValues]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  /**
   * ACRESCENTA o cliente escolhido, em vez de trocar o anterior.
   *
   * A busca de cliente devolve um por vez -- é um campo de pesquisa, não uma
   * lista com caixas --, então quem acumula é esta função. Repetido não entra:
   * procurar o mesmo registro duas vezes não pode virar dois chips.
   */
  const handleClientSelect = (client) => {
    if (!client) return;

    setFilters((prev) => {
      const atuais = comoLista(prev.client);

      if (atuais.some((c) => mesmoCliente(c, client))) return prev;

      return { ...prev, client: atuais.concat([client]) };
    });
  };

  const handleApply = () => {
    if (onApply) {
      const payload = fixedClient ? { ...filters, client: [fixedClient] } : filters;

      /*
       * Formulário em branco é "sem busca avançada", e não "busca avançada
       * vazia".
       *
       * "Aplicar Filtros" não tem `disabled` (só "Limpar Filtros" tem), então
       * abrir o modal, limpar e aplicar mandava um objeto com todos os campos
       * vazios -- que é TRUTHY. A tela então passava a acreditar que havia um
       * refinamento: o chip "Busca avançada ativa" aparecia sem nada
       * filtrado, e, pior, a ORDENAÇÃO ficava desabilitada
       * ("a lista está refinada aqui no navegador"), sem que houvesse
       * refinamento nenhum. Encontrado ao validar o ponto 11 em produção, em
       * 28/10/2026.
       *
       * Com `fixedClient` o objeto vai do mesmo jeito: ali o cliente é o
       * recorte da tela (ClientTicketsPage) e `hasActiveFilters` o ignora de
       * propósito -- mandar `null` perderia o recorte.
       */
      onApply(fixedClient || hasActiveFilters() ? payload : null);
    }
    onClose();
  };

  const handleClear = () => {
    const clearedFilters = fixedClient ? { ...emptyFilterState, client: [fixedClient] } : { ...emptyFilterState };
    setFilters(clearedFilters);
    if (onClear) {
      onClear();
    }
  };

  /**
   * Os departamentos escolhidos, por NOME, para a caixa fechada.
   *
   * Sem isto o `Select` múltiplo mostra os ids crus ("3, 7") e o marcador
   * dinâmico inteiro -- quem está olhando não reconhece nenhum dos dois.
   */
  const rotulosDosGrupos = (selecionados) => comoLista(selecionados)
    .map((valor) => {
      if (String(valor) === VALOR_GRUPO_DO_AGENTE) return ROTULOS[VALOR_GRUPO_DO_AGENTE];

      const grupo = (serviceGroups || []).find((g) => String(g.id) === String(valor));

      return grupo ? (grupo.description || `Grupo #${grupo.id}`) : String(valor);
    })
    .join(', ');

  // Opções de status vindas dos status realmente cadastrados (value = status_id).
  //
  // "Não concluído" vem na FRENTE, e é um marcador de texto no lugar do id --
  // o mesmo que o filtro salvo usa, para a pessoa ver a mesma escolha nos
  // dois lugares. Na frente porque é a pergunta mais feita ("o que falta
  // fazer?"); no meio da lista de status, ninguém acha.
  const statusOptions = [
    { value: VALOR_STATUS_NAO_CONCLUIDO, label: ROTULOS[VALOR_STATUS_NAO_CONCLUIDO] },
    ...[...(ticketStatuses || [])]
      .sort((a, b) => (a.order ?? a.id ?? 0) - (b.order ?? b.id ?? 0))
      .map((s) => ({ value: s.id, label: s.name })),
  ];

  const priorityOptions = [
    { value: 0, label: 'Baixa' },
    { value: 1, label: 'Média' },
    { value: 2, label: 'Alta' },
    { value: 3, label: 'Urgente' }
  ];

  const hasActiveFilters = () => {
    const clientCounts = !fixedClient && temSelecao(filters.client);
    return !!(
      filters.title ||
      filters.status ||
      // `temSelecao`, e não o valor cru: lista VAZIA é truthy, e o botão de
      // aplicar passaria a achar que há filtro quando não há.
      temSelecao(filters.priority) ||
      filters.assignedTo ||
      filters.createdBy ||
      clientCounts ||
      temSelecao(filters.category) ||
      temSelecao(filters.serviceId) ||
      filters.product ||
      filters.linhaDoProduto ||
      filters.representative ||
      filters.contact ||
      temSelecao(filters.channel) ||
      filters.ultimaAcao ||
      filters.textoNaAcao ||
      filters.ccUsers ||
      filters.createdAtStart ||
      filters.createdAtEnd ||
      filters.resolvedAtStart ||
      filters.resolvedAtEnd ||
      filters.updatedAtStart ||
      filters.updatedAtEnd ||
      temSelecao(filters.serviceGroupId)
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            height: 'auto'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
          pb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon color="primary" />
            <Typography variant="h6" component="span">
              {fixedClient ? 'Refinar tickets deste cliente' : 'Busca Avançada de Tickets'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2, bgcolor: 'transparent' }}>
          <Box sx={{ 
            maxHeight: 'calc(90vh - 200px)', 
            overflowY: 'auto',
            px: 2,
            py: 1,
            bgcolor: 'transparent'
          }}>
            <Grid container spacing={2} sx={{ bgcolor: 'transparent' }}>
              {/* Seção: Por Status e Prioridade */}
              <Grid item xs={12} sx={{ bgcolor: 'transparent' }}>
                <Typography variant="subtitle1" sx={{ mt: 1, mb: 1, fontWeight: 600, color: 'text.primary' }}>
                  Por Status e Prioridade
                </Typography>
              </Grid>

              {/* Status */}
              <Grid item xs={12} md={6} sx={{ mb: 1, bgcolor: 'transparent' }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">
                      <em>Todos</em>
                    </MenuItem>
                    {statusOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Prioridade */}
              <Grid item xs={12} md={6} sx={{ mb: 1, bgcolor: 'transparent' }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Prioridade</InputLabel>
                  <Select
                    multiple
                    value={comoLista(filters.priority)}
                    onChange={(e) => handleFilterChange('priority', e.target.value)}
                    label="Prioridade"
                    renderValue={(marcados) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {marcados.map((valor) => (
                          <Chip
                            key={valor}
                            size="small"
                            label={(priorityOptions.find((o) => o.value === valor) || {}).label || valor}
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {priorityOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        <Checkbox size="small" checked={comoLista(filters.priority).indexOf(option.value) > -1} />
                        <ListItemText primary={option.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Produto */}
              <Grid item xs={12} md={6} sx={{ mb: 1, bgcolor: 'transparent' }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Produto</InputLabel>
                  <Select
                    value={filters.product}
                    onChange={(e) => handleFilterChange('product', e.target.value)}
                    label="Produto"
                  >
                    <MenuItem value=""><em>Todos</em></MenuItem>
                    {products.map((p) => (
                      <MenuItem key={p} value={p}>{p}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Linha do produto — Sysfar, Max, Next (K3) */}
              <Grid item xs={12} md={6} sx={{ mb: 1, bgcolor: 'transparent' }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Linha do produto</InputLabel>
                  <Select
                    value={filters.linhaDoProduto}
                    onChange={(e) => handleFilterChange('linhaDoProduto', e.target.value)}
                    label="Linha do produto"
                  >
                    <MenuItem value=""><em>Todas</em></MenuItem>
                    {/* Uma opção por linha: "MAX Ouro" e "MAX Ilimitado"
                        aparecem no Produto acima como duas, e aqui como uma. */}
                    {productLines.map((l) => (
                      <MenuItem key={l} value={l}>{l}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Representante */}
              <Grid item xs={12} md={6} sx={{ mb: 1, bgcolor: 'transparent' }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Representante</InputLabel>
                  <Select
                    value={filters.representative}
                    onChange={(e) => handleFilterChange('representative', e.target.value)}
                    label="Representante"
                  >
                    <MenuItem value=""><em>Todos</em></MenuItem>
                    {representatives.map((r) => (
                      <MenuItem key={r} value={r}>{r}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Divisor */}
              <Grid item xs={12} sx={{ bgcolor: 'transparent' }}>
                <Divider sx={{ my: 2 }} />
              </Grid>

              {/* Seção: Por Conteúdo */}
              <Grid item xs={12} sx={{ bgcolor: 'transparent' }}>
                <Typography variant="subtitle1" sx={{ mt: 1, mb: 1, fontWeight: 600, color: 'text.primary' }}>
                  Por Conteúdo
                </Typography>
              </Grid>

              {/* Assunto */}
              <Grid item xs={12} md={6} sx={{ mb: 1, bgcolor: 'transparent' }}>
                <TextField
                  fullWidth
                  label="Assunto"
                  value={filters.title}
                  onChange={(e) => handleFilterChange('title', e.target.value)}
                  variant="outlined"
                  size="small"
                />
              </Grid>

              {/* Texto na ação */}
              <Grid item xs={12} md={6} sx={{ mb: 1, bgcolor: 'transparent' }}>
                {/*
                  * Junto do Assunto porque responde a mesma pergunta -- "onde
                  * isto foi escrito?" --, só que dentro da conversa em vez do
                  * título.
                  *
                  * O aviso não é decoração: este é o ÚNICO campo da Busca
                  * Avançada que o navegador não sabe avaliar (a listagem não
                  * traz as ações). Preenchê-lo faz a lista vir do servidor, e
                  * quem não souber disso vai estranhar a lista mudar de
                  * natureza.
                  */}
                <TextField
                  fullWidth
                  label="Texto na ação"
                  placeholder="Palavra escrita dentro de uma ação"
                  value={filters.textoNaAcao}
                  onChange={(e) => handleFilterChange('textoNaAcao', e.target.value)}
                  variant="outlined"
                  size="small"
                  helperText="Procura no texto das ações do chamado, inclusive nos já fechados."
                  inputProps={{ 'data-testid': 'texto-na-acao' }}
                />
              </Grid>

              {/* Categoria */}
              <Grid item xs={12} md={6} sx={{ mb: 1, bgcolor: 'transparent' }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Categoria</InputLabel>
                  <Select
                    multiple
                    value={comoLista(filters.category)}
                    onChange={(e) => handleFilterChange('category', e.target.value)}
                    label="Categoria"
                    renderValue={(marcados) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {marcados.map((valor) => (
                          <Chip key={valor} size="small" label={valor} />
                        ))}
                      </Box>
                    )}
                  >
                    {categories.map(category => (
                      <MenuItem key={category} value={category}>
                        <Checkbox size="small" checked={comoLista(filters.category).indexOf(category) > -1} />
                        <ListItemText primary={category} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Serviço */}
              <Grid item xs={12} md={6} sx={{ mb: 1, bgcolor: 'transparent' }}>
                <Typography variant="body2" sx={{ mb: 0.75, color: 'text.secondary', fontWeight: 500 }}>
                  Serviço
                </Typography>
                {/*
                  * `value` vai CRU, e não normalizado aqui: `comoLista` dentro
                  * do seletor devolve um array novo a cada chamada, e passá-lo
                  * daqui criaria uma identidade nova a cada render -- o efeito
                  * que abre os pais marcados rodaria sem parar.
                  */}
                <ServicePicker
                  multiple
                  services={services}
                  value={filters.serviceId}
                  onChange={(ids) => handleFilterChange('serviceId', ids)}
                  allowClear
                  emptyLabel="Todos os serviços"
                  placeholder="Todos os serviços"
                />
              </Grid>

              {/* Contato */}
              <Grid item xs={12} md={6} sx={{ mb: 1, bgcolor: 'transparent' }}>
                <Autocomplete
                  freeSolo
                  noOptionsText="Nenhuma opção"
                  options={contacts}
                  value={filters.contact}
                  onChange={(event, newValue) => handleFilterChange('contact', newValue || '')}
                  onInputChange={(event, newInputValue) => handleFilterChange('contact', newInputValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Contato"
                      variant="outlined"
                      size="small"
                    />
                  )}
                />
              </Grid>

              {/* Canal */}
              <Grid item xs={12} md={6} sx={{ mb: 1, bgcolor: 'transparent' }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Canal</InputLabel>
                  <Select
                    multiple
                    value={comoLista(filters.channel)}
                    onChange={(e) => handleFilterChange('channel', e.target.value)}
                    label="Canal"
                    renderValue={(marcados) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {marcados.map((valor) => (
                          <Chip key={valor} size="small" label={valor} />
                        ))}
                      </Box>
                    )}
                  >
                    {channels.map(channel => (
                      <MenuItem key={channel} value={channel}>
                        <Checkbox size="small" checked={comoLista(filters.channel).indexOf(channel) > -1} />
                        <ListItemText primary={channel} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Última ação — de que lado está a bola */}
              <Grid item xs={12} md={6} sx={{ mb: 1, bgcolor: 'transparent' }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Última ação</InputLabel>
                  <Select
                    value={filters.ultimaAcao}
                    onChange={(e) => handleFilterChange('ultimaAcao', e.target.value)}
                    label="Última ação"
                  >
                    <MenuItem value="">
                      <em>Qualquer</em>
                    </MenuItem>
                    {/* Os valores saem de `UltimaAcaoDoChamado` na api e viajam
                        prontos em `ticket.ultima_acao` -- aqui so se compara. */}
                    <MenuItem value="cliente">Do cliente</MenuItem>
                    <MenuItem value="operador">Do operador</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Divisor */}
              <Grid item xs={12} sx={{ bgcolor: 'transparent' }}>
                <Divider sx={{ my: 2 }} />
              </Grid>

              {/* Seção: Por Pessoa Envolvida */}
              <Grid item xs={12} sx={{ bgcolor: 'transparent' }}>
                <Typography variant="subtitle1" sx={{ mt: 1, mb: 1, fontWeight: 600, color: 'text.primary' }}>
                  Por Pessoa Envolvida
                </Typography>
              </Grid>

              {/* Responsável */}
              <Grid item xs={12} md={6} sx={{ mb: 1, bgcolor: 'transparent' }}>
                <Autocomplete
                  noOptionsText="Nenhuma opção"
                  // Mesmas escolhas do filtro salvo ("Agente logado", "Sem
                  // responsável"), pelo mesmo caminho: pseudo-opções no topo,
                  // tratadas como qualquer outra pelo `<Autocomplete>`.
                  options={opcoesComDinamicos('assigned_to', users)}
                  getOptionLabel={(option) => option.name || ''}
                  value={filters.assignedTo}
                  onChange={(event, newValue) => handleFilterChange('assignedTo', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Responsável"
                      variant="outlined"
                      size="small"
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Typography>{option.name}</Typography>
                    </Box>
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                />
              </Grid>

              <Grid item xs={12} md={6} sx={{ mb: 1, bgcolor: 'transparent' }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Grupo de atendimento</InputLabel>
                  {/*
                    * MAIS DE UM departamento (ponto 11 da Gisele, continuação):
                    * "não precisar criar vários Kanbans só para mudar a
                    * combinação de filtros".
                    *
                    * Os valores viajam como TEXTO, e a conversão para número
                    * fica no filtro, e não aqui. O marcador dinâmico convive
                    * na mesma lista -- `Number(marcador)` seria `NaN`, e
                    * converter na escolha misturaria os dois tipos no estado.
                    *
                    * Sem o item "Todos": em escolha múltipla, nada marcado JÁ
                    * é "todos" -- e um item que desmarca os outros é uma
                    * terceira regra para quem usa a tela aprender.
                    */}
                  <Select
                    multiple
                    value={comoLista(filters.serviceGroupId).map(String)}
                    onChange={(e) => {
                      const escolhidos = e.target.value;
                      handleFilterChange(
                        'serviceGroupId',
                        (Array.isArray(escolhidos) ? escolhidos : [escolhidos]).map(String)
                      );
                    }}
                    label="Grupo de atendimento"
                    renderValue={(selecionados) => rotulosDosGrupos(selecionados)}
                  >
                    <MenuItem value={VALOR_GRUPO_DO_AGENTE}>
                      <Checkbox
                        size="small"
                        checked={comoLista(filters.serviceGroupId).map(String).indexOf(VALOR_GRUPO_DO_AGENTE) > -1}
                      />
                      <ListItemText primary={ROTULOS[VALOR_GRUPO_DO_AGENTE]} />
                    </MenuItem>
                    {serviceGroups.map((g) => (
                      <MenuItem key={g.id} value={String(g.id)}>
                        <Checkbox
                          size="small"
                          checked={comoLista(filters.serviceGroupId).map(String).indexOf(String(g.id)) > -1}
                        />
                        <ListItemText primary={g.description || `Grupo #${g.id}`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Criado por */}
              <Grid item xs={12} md={6} sx={{ mb: 1, bgcolor: 'transparent' }}>
                <Autocomplete
                  noOptionsText="Nenhuma opção"
                  options={opcoesComDinamicos('created_by', users)}
                  getOptionLabel={(option) => option.name || ''}
                  value={filters.createdBy}
                  onChange={(event, newValue) => handleFilterChange('createdBy', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Criado por"
                      variant="outlined"
                      size="small"
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Typography>{option.name}</Typography>
                    </Box>
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                />
              </Grid>

              {/* Cliente */}
              <Grid item xs={12} md={6} sx={{ mb: 1, bgcolor: 'transparent' }}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>
                    Cliente
                  </Typography>
                  {fixedClient ? (
                    <Chip
                      label={
                        fixedClient.company?.business_name ||
                        fixedClient.company?.name ||
                        fixedClient.name ||
                        `Cliente #${fixedClient.id}`
                      }
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <>
                      <ClientSearch
                        onClientSelect={handleClientSelect}
                        selectedClient={filters.client}
                        placeholder="Buscar cliente..."
                        enableClientTicketsShortcut={false}
                      />
                      {/*
                        * Um chip por cliente, cada um com o seu X. É a única
                        * forma de tirar UM da lista: a busca acrescenta, e sem
                        * o X individual a pessoa teria de limpar tudo e
                        * procurar os outros de novo.
                        */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {comoLista(filters.client).map((cliente, posicao) => (
                          <Chip
                            key={cliente?.id != null ? `cliente-${cliente.id}` : `cliente-pos-${posicao}`}
                            label={rotuloDoCliente(cliente)}
                            onDelete={() =>
                              handleFilterChange(
                                'client',
                                comoLista(filters.client).filter((c) => !mesmoCliente(c, cliente))
                              )
                            }
                            size="small"
                          />
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              </Grid>

              {/* CC - Cópia para acompanhamento */}
              <Grid item xs={12} md={6} sx={{ mb: 1, bgcolor: 'transparent' }}>
                <Autocomplete
                  noOptionsText="Nenhuma opção"
                  options={users || []}
                  getOptionLabel={(option) => option.name || ''}
                  value={filters.ccUsers}
                  onChange={(event, newValue) => handleFilterChange('ccUsers', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="CC (Cópia para acompanhamento)"
                      variant="outlined"
                      size="small"
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Typography>{option.name}</Typography>
                    </Box>
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                />
              </Grid>

              {/* Divisor */}
              <Grid item xs={12} sx={{ bgcolor: 'transparent' }}>
                <Divider sx={{ my: 2 }} />
              </Grid>

              {/* Seção: Por Datas */}
              <Grid item xs={12} sx={{ bgcolor: 'transparent' }}>
                <Typography variant="subtitle1" sx={{ mt: 1, mb: 1, fontWeight: 600, color: 'text.primary' }}>
                  Por Datas
                </Typography>
                {/*
                  * Atalhos de período (itens 52/9): preenchem a data de
                  * abertura.
                  *
                  * "Ontem", "Semana passada" e "Mês anterior" entraram em
                  * 14/10/2026, a pedido -- os três já existiam em
                  * `utils/datePeriods` e no seletor do filtro salvo, e só esta
                  * barra não os oferecia. Os rótulos vêm de lá, e não escritos
                  * à mão, para a mesma opção não ter dois nomes na mesma tela.
                  */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 0.5 }}>
                  {['today', 'yesterday', 'this_week', 'last_week', 'this_month', 'previous_month', 'last_7_days', 'last_30_days'].map((op) => (
                    <Chip
                      key={op}
                      label={DATE_PERIOD_LABELS[op]}
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const range = resolveDatePeriodRange(op);
                        if (!range) return;
                        handleFilterChange('createdAtStart', new Date(range.startMs));
                        handleFilterChange('createdAtEnd', new Date(range.endMs));
                      }}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Grid>

              {/* Data de abertura - Início */}
              <Grid item xs={12} md={6} sx={{ mb: 1, bgcolor: 'transparent' }}>
                <DatePicker
                  label="Data de abertura - Início"
                  value={filters.createdAtStart}
                  onChange={(newValue) => handleFilterChange('createdAtStart', newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      variant: 'outlined'
                    }
                  }}
                />
              </Grid>

              {/* Data de abertura - Fim */}
              <Grid item xs={12} md={6} sx={{ mb: 1, bgcolor: 'transparent' }}>
                <DatePicker
                  label="Data de abertura - Fim"
                  value={filters.createdAtEnd}
                  onChange={(newValue) => handleFilterChange('createdAtEnd', newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      variant: 'outlined'
                    }
                  }}
                />
              </Grid>

              {/* Data de resolução - Início */}
              <Grid item xs={12} md={6} sx={{ mb: 1, bgcolor: 'transparent' }}>
                <DatePicker
                  label="Data de resolução - Início"
                  value={filters.resolvedAtStart}
                  onChange={(newValue) => handleFilterChange('resolvedAtStart', newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      variant: 'outlined'
                    }
                  }}
                />
              </Grid>

              {/* Data de resolução - Fim */}
              <Grid item xs={12} md={6} sx={{ mb: 1, bgcolor: 'transparent' }}>
                <DatePicker
                  label="Data de resolução - Fim"
                  value={filters.resolvedAtEnd}
                  onChange={(newValue) => handleFilterChange('resolvedAtEnd', newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      variant: 'outlined'
                    }
                  }}
                />
              </Grid>

              {/* Data de Atualização - Início */}
              <Grid item xs={12} md={6} sx={{ mb: 1, bgcolor: 'transparent' }}>
                <DatePicker
                  label="Data de Atualização - Início"
                  value={filters.updatedAtStart}
                  onChange={(newValue) => handleFilterChange('updatedAtStart', newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      variant: 'outlined'
                    }
                  }}
                />
              </Grid>

              {/* Data de Atualização - Fim */}
              <Grid item xs={12} md={6} sx={{ mb: 1, bgcolor: 'transparent' }}>
                <DatePicker
                  label="Data de Atualização - Fim"
                  value={filters.updatedAtEnd}
                  onChange={(newValue) => handleFilterChange('updatedAtEnd', newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      variant: 'outlined'
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ 
          borderTop: `1px solid ${theme.palette.divider}`,
          pt: 2,
          px: 3,
          pb: 2
        }}>
          <Button
            onClick={handleClear}
            color="secondary"
            variant="outlined"
            disabled={!hasActiveFilters()}
          >
            Limpar Filtros
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose} variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            color="primary"
          >
            Aplicar Filtros
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

const mapStateToProps = (state) => {
  const { user } = state;
  return {
    users: user.users || []
  };
};

const mapDispatchToProps = (dispatch) => ({
  getUsers: () => dispatch(getUsers())
});

export default connect(mapStateToProps, mapDispatchToProps)(AdvancedSearchModal);
